/**
 * ArloService.js - Thin wrapper around @arlotv/embed-core for the Arlo rail
 *
 * Owns the singleton embed instance (the SDK bakes customerId into the
 * instance, so it's cached per customerId and rebuilt if the customer
 * changes). Every failure path in getRails resolves to { rails: [] } so
 * callers never need try/catch — the SDK's rails() rejects on non-2xx.
 */
import { createArloEmbed } from '@arlotv/embed-core';
import { clientEnv } from '../buildTimeEnv';

// The SDK exposes no timeout/abort option, so the call is raced against this.
// Losing the race abandons the request rather than aborting it — same
// observable behavior as the old AbortController for the rail (it either
// appears in time or never does).
//
// Arlo serves a signals cache hit in ~250ms, but a miss recomposes the LLM home
// layout, which measured 6.5-11.5s live. Signals change whenever the user
// favorites/likes/dislikes anything, so a budget under that drops the rail for
// precisely the users who just interacted with it. Sized for the cold path.
const REQUEST_TIMEOUT_MS = 15000;
const EMPTY_RESULT = { rails: [] };
const NOOP_CHAT_HANDLE = { cancel: () => {} };
const EMPTY_SIMILAR = { items: [] };

class ArloService {
  _embed = null;
  _embedCustomerId = null;

  /**
   * The embed for `customerId`, rebuilt when the customer changes (the SDK
   * bakes customerId into the instance).
   *
   * `customerId` may be undefined — that is the signed-out PDP, where
   * similar() is still valid: it is never personalized, is cached on
   * (content_id, country), and omitting customer_id costs only the attribution
   * ledger entry. ArloEmbedConfig types customerId as required, but
   * createArloEmbed never reads it beyond handing it to the request builders
   * and similar() drops customer_id when it is absent.
   *
   * rails() and chat() must NOT be reached this way — both need a real
   * customer, and both callers gate on one before calling.
   *
   * customerId is the active *profile* id (see arloIdentity.js), so switching
   * profiles swaps the embed. Flush the outgoing profile's buffered telemetry
   * before replacing it — a batched event must attribute to the profile that
   * generated it, never leak onto the next profile or vanish on switch.
   */
  _getEmbed(customerId) {
    const apiKey = clientEnv.REACT_APP_ARLO_PUBLISHABLE_KEY;
    if (!apiKey) return null;

    if (!this._embed || this._embedCustomerId !== customerId) {
      if (this._embed) this._embed.flush();
      this._embed = createArloEmbed({ apiKey, customerId });
      this._embedCustomerId = customerId;
    }
    return this._embed;
  }

  async getRails({ customerId, favorites = [], likes = [], dislikes = [], watched = [] }) {
    if (!customerId) return EMPTY_RESULT;
    const embed = this._getEmbed(customerId);
    if (!embed) return EMPTY_RESULT;

    let timeoutId;
    try {
      const data = await Promise.race([
        embed.rails({ favorites, likes, dislikes, watched }, { country: 'US' }),
        new Promise((_, reject) => {
          timeoutId = setTimeout(
            () => reject(new Error('rails request timed out')),
            REQUEST_TIMEOUT_MS,
          );
        }),
      ]);

      if (!data || !Array.isArray(data.rails)) {
        return EMPTY_RESULT;
      }
      return data;
    } catch (error) {
      console.warn('ArloService: getRails failed:', error.message || error.code || error);
      return EMPTY_RESULT;
    } finally {
      clearTimeout(timeoutId);
    }
  }

  /**
   * Content-to-content "More Like This" for a PDP, via the SDK's similar().
   *
   * `customerId` is optional and attribution-only (chill-backend#542): it lets
   * a later PDP click attribute back to this impression, but never affects
   * ranking or the (content_id, country) cache key. Signed-out visitors have
   * none — the rail still renders identically, the ledger just skips that view.
   *
   * The try/catch is load-bearing despite the integration guide calling similar
   * "fail-silent — never throws". That only holds for an unknown title, where
   * the server answers 200 with a `reason`. The SDK throws
   * { code: 'similar_http_<status>' } on ANY non-2xx, and a 429 is reachable
   * under the publishable key's 60 req/min cap. Every failure path here still
   * resolves to { items: [] } so callers never try/catch.
   */
  async similar(contentId, { country = 'US', max = 12, customerId } = {}) {
    if (!contentId) return EMPTY_SIMILAR;
    const embed = this._getEmbed(customerId);
    if (!embed) return EMPTY_SIMILAR;

    let timeoutId;
    try {
      const data = await Promise.race([
        embed.similar({ contentId, country, max, customerId }),
        new Promise((_, reject) => {
          timeoutId = setTimeout(
            () => reject(new Error('similar request timed out')),
            REQUEST_TIMEOUT_MS,
          );
        }),
      ]);

      if (!data || !Array.isArray(data.items)) return EMPTY_SIMILAR;
      return {
        items: data.items,
        attribution: data.attribution || null,
        reason: data.reason || null,
      };
    } catch (error) {
      console.warn('ArloService: similar failed:', error.message || error.code || error);
      return EMPTY_SIMILAR;
    } finally {
      clearTimeout(timeoutId);
    }
  }

  /**
   * Create (or reuse) the embed for this customer without issuing a request.
   * The chat panel calls this on open so track('chat_opened') has an
   * instance to buffer into. False when the key is unset, or when there is no
   * customerId — chat is signed-in only.
   */
  ensureEmbed(customerId) {
    if (!customerId) return false;
    return Boolean(this._getEmbed(customerId));
  }

  /**
   * Streaming chat passthrough. Same fail-silent contract as getRails: when
   * the feature is unconfigured (or the SDK throws synchronously) callers
   * get a working no-op handle — results only ever arrive via the callbacks.
   * Note chat() is synchronous (returns a handle, not a Promise); the SDK's
   * own transport ladder and cancel-guards handle async failure paths.
   */
  chat(customerId, message, opts = {}) {
    // Signed-in only: a customer-less embed would send customer_id: undefined.
    if (!customerId) return NOOP_CHAT_HANDLE;
    const embed = this._getEmbed(customerId);
    if (!embed) return NOOP_CHAT_HANDLE;

    try {
      return embed.chat(message, opts);
    } catch (error) {
      console.warn('ArloService: chat failed to start:', error.message || error);
      opts.onError?.({ code: 'chat_start_failed', message: 'chat failed to start' });
      return NOOP_CHAT_HANDLE;
    }
  }

  track(type, data) {
    this._embed?.track(type, data);
  }

  attributionRendered() {
    this._embed?.attributionRendered();
  }

  flush() {
    this._embed?.flush();
  }
}

const arloService = new ArloService();

// Telemetry batches must survive navigation. Module scope = registered once
// app-wide; flush() is a no-op until an embed instance exists.
if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', () => arloService.flush());
}

export default arloService;
